import { useEffect, useState, useCallback, useRef } from "react";
import API_ROUTES from "~/api-routes";
import useDebounce from "~/hooks/useDebounce";
import apiService from "~/services/service";
import z from "zod";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const useOfficersAndAttornyRegistry = ({ type }) => {
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [totalRecord, setTotalRecords] = useState(0);
  const [isUserRegestered, setIsUserRegistered] = useState(false);
  const [sortMeta, setSortMeta] = useState([{ field: "name", order: 1 }]);
  const debouncedSearchTerm = useDebounce(search, 500);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  // GXM-1767: Track if we need to skip cache (e.g., after creating a new officer/POA)
  const skipCacheRef = useRef(location.state?.skipCache || false);
  const fetchData = useCallback(
    async ({
      pageIndex = page,
      pageSize = rows,
      searchValue = search,
      sortBy = sortMeta?.[0]?.field ?? "name",
      sortOrder = sortMeta?.[0]?.order === 1 ? "ASC" : "DESC",
      skipCache = false,
    } = {}) => {
      setLoading(true);
      setIsDataAvailable(true);

      try {
        const { data } = await apiService(API_ROUTES.FETCH_OFFICERS, "GET", {
          replace: {
            entity_id: id,
            page: pageIndex + 1,
            pageSize,
            searchValue,
            type,
            sortBy,
            sortOrder,
          },
          skipCache,
          schema: z.object({
            page: z.number().optional(),
            pageSize: z.number().optional(),
            search: z.string().optional(),
            type: z.enum(["officer", "power-of-attorney"]),
            sortBy: z.enum(["name", "updatedAt", "role"]).optional(),
            sortOrder: z.enum(["ASC", "DESC"]).optional(),
          }),
        });
        setTotalRecords(data?.totalRecords);
        if (data?.records?.length) {
          setData(data?.records);
          setIsDataAvailable(true);
        } else {
          if (search.length) {
            setIsUserRegistered(true);
          } else {
            setIsUserRegistered(false);
          }
          setData([]);
          setIsDataAvailable(false);
        }
      } catch (error) {
        setData([]);
        setIsDataAvailable(false);
      } finally {
        setLoading(false);
      }
    },
    [type, page, rows, debouncedSearchTerm, sortMeta],
  );

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    // GXM-1767: Use skipCache on first fetch if navigating back after creation
    const shouldSkipCache = skipCacheRef.current;
    fetchData({ pageIndex: page, pageSize: rows, skipCache: shouldSkipCache });
    // Reset skipCache after first use
    if (shouldSkipCache) {
      skipCacheRef.current = false;
    }
  }, [page, rows, debouncedSearchTerm, fetchData, sortMeta]);

  const onPageChange = ({ page, rows }) => {
    setPage(page);
    setRows(rows);
  };
  const onSort = (e) => {
    setSortMeta(e.multiSortMeta);
  };
  const handleNavigate = () => {
    if (type === "officer") {
      navigate(`/entity/${id}/officers/add`);
    } else {
      navigate(`/entity/${id}/powers-of-attorney/add`);
    }
  };

  return {
    search,
    setSearchQuery: setSearch,
    data,
    loading,
    isDataAvailable,
    totalRecord,
    onPageChange,
    rows,
    page,
    isUserRegestered,
    sortMeta,
    onSort,
    handleNavigate,
  };
};

export default useOfficersAndAttornyRegistry;
